// Foutpagina van de wachtwoordloze inloglink (knop-1b-sso, 30-08).
//
// Waarom deze pagina in het portaal woont en niet in de checkout:
//
// - het is een gewone deploy, geen thema-plaatsing;
// - het raakt mock v5 niet, dus geen extra mock-afwijking;
// - de klant staat op dit moment sowieso buiten de shop: hij komt uit
//   zijn mailbox.
//
// De klant komt hier alleen als zijn link niet meer werkt. Aan de
// shopkant zijn "verlopen" en "verbruikt" niet te onderscheiden (en er
// is geen leesweg op CustomerTokens), maar onze eigen linkrij weet het
// wél — daarom is die rij de bron van waarheid en kan deze pagina drie
// eerlijke standen tonen in plaats van één vage.
//
// De pagina toont nooit het mailadres, nooit het token en nooit een
// klantnaam, en draagt noindex en no-store.

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import voeronlineLogo from "@/assets/voeronline-logo.png";

// Constante, net als de returnUrl aan de serverkant: deze knop hoort
// nooit ergens anders heen te kunnen wijzen dan naar de eigen checkout.
const CHECKOUT_URL = "https://www.voeronline.nl/checkout/";
const SHOP_URL = "https://www.voeronline.nl";
const ORANJE = "#ef7f01";

// De klant komt hier uit zijn mailbox en was net bezig met afrekenen.
// Daarom volgt deze pagina de 1b-schermen uit de checkout en niet het
// retourportaal: één smalle kolom, logo erboven, rustige typografie en
// een kale voetregel — geen USP-balk, geen chatknop, geen kaartvlak.
// Dezelfde maten als scherm 1b: kolom 520, kop 22, tekst 15.
const KOLOM = "mx-auto w-full max-w-[520px] px-5";

const STANDEN = ["verlopen", "gebruikt", "storing"] as const;
type Stand = (typeof STANDEN)[number];

// Een onbekende of ontbrekende status valt terug op "storing": dat is
// de enige stand die altijd waar kan zijn.
function leesStand(waarde: string | null): Stand {
  return STANDEN.includes(waarde as Stand) ? (waarde as Stand) : "storing";
}

export function InloglinkPagina() {
  const { t } = useTranslation();
  const [zoekParams] = useSearchParams();
  const stand = leesStand(zoekParams.get("status"));

  // Deze pagina hoort niet in een zoekmachine en niet in een cache.
  // De server zet no-store op de redirect hierheen; de meta-tag is de
  // kant die de browser zelf leest.
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => {
      document.head.removeChild(robots);
    };
  }, []);

  return (
    <div className="bg-white font-[\'Nunito_Sans\',sans-serif]">
      <header className="border-b border-[#e6e8ec]">
        <div className={`${KOLOM} flex h-[72px] items-center justify-center`}>
          <a href={SHOP_URL} className="inline-flex shrink-0">
            <img
              src={voeronlineLogo}
              alt="VoerOnline"
              className="h-9 w-auto"
              draggable={false}
            />
          </a>
        </div>
      </header>

      <main className={`${KOLOM} pb-12 pt-9`}>
        <div data-testid={`inloglink-${stand}`}>
          <h1 className="text-[22px] font-bold leading-snug text-[#111111]">
            {t(`inloglink.${stand}_titel`)}
          </h1>
          <p className="mt-2.5 text-[15px] leading-[1.6] text-[#5a6270]">
            {t(`inloglink.${stand}`)}
          </p>
          <a
            href={CHECKOUT_URL}
            data-testid="inloglink-terug"
            className="mt-7 block rounded-[10px] py-3.5 text-center text-[15px] font-bold text-white"
            style={{ background: ORANJE }}
          >
            {t("inloglink.terug")}
          </a>
        </div>
      </main>

      <footer className="border-t border-[#e6e8ec]">
        <div
          className={`${KOLOM} flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 py-5 text-[13px] text-[#5a6270]`}
        >
          <a href={`${SHOP_URL}/service/`} className="text-[#0a7cc7]">
            {t("inloglink.voet_klantenservice")}
          </a>
          <a
            href={`${SHOP_URL}/service/general-terms-conditions/`}
            className="text-[#0a7cc7]"
          >
            {t("inloglink.voet_voorwaarden")}
          </a>
          <a
            href={`${SHOP_URL}/service/privacy-policy/`}
            className="text-[#0a7cc7]"
          >
            {t("inloglink.voet_privacy")}
          </a>
          <span>{t("inloglink.voet_copyright")}</span>
        </div>
      </footer>
    </div>
  );
}
